import { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Eye, EyeOff, Mail, Lock, ArrowRight, ArrowLeft } from "lucide-react";
import { Footer } from "../components/Footer";
import { useApp } from "../App";
import { getLogoImageUrl } from "@/services/utlits";
import { GoogleOAuthProvider, GoogleLogin } from "@react-oauth/google";

export function LoginPage() {
  const GoogleClientId = import.meta.env.VITE_GOOGLE_CLIENT_ID;
  const { login, loginWithGoogle, continueAsGuest, restaurant, isLoggedIn } =
    useApp();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const redirectTo = searchParams.get("redirect") || "/";

  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [errorMessage, setErrorMessage] = useState("");

  useEffect(() => {
    if (isLoggedIn) {
      navigate(
        redirectTo === "checkout"
          ? "/checkout"
          : redirectTo === "profile"
          ? "/profile"
          : "/"
      );
    }
  }, [isLoggedIn, navigate, redirectTo]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setErrorMessage("");
    var result = await login(email, password);
    if (!result || !result.success) {
      setErrorMessage(result.error);
    }
    setIsLoading(false);
  };

  const handleGoogleLogin = async (credentialResponse: any) => {
    setIsLoading(true);

    if (!credentialResponse.credential) {
      alert("Google login failed");
      setIsLoading(false);
      return;
    }

    try {
      const dto = { idToken: credentialResponse.credential };
      await loginWithGoogle(dto);
    } catch (error) {
      alert("Login error");
    } finally {
      setIsLoading(false);
    }
  };

  const handleGuestLogin = async () => {
    setIsLoading(true);
    await continueAsGuest();
    setIsLoading(false);
  };

  const logoUrl = getLogoImageUrl("pixwipizza.png");

  return (
    <>
      <div className="min-h-screen relative overflow-hidden">
        {/* Background Image */}
        <div className="absolute inset-0">
          <img
            src="https://images.pexels.com/photos/1640777/pexels-photo-1640777.jpeg?auto=compress&cs=tinysrgb&w=1600"
            alt="Thai Food Background"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-black/60" />
        </div>

        <div className="relative z-10 min-h-screen bg-gradient-to-br from-emerald-900/20 via-transparent to-thai-900/20 flex items-center justify-center py-8 sm:py-12 mobile-padding">
          <div className="max-w-md w-full space-y-6 sm:space-y-8 animate-fade-in-up">
            {/* Back Button */}
            <div className="flex items-center animate-slide-in-left">
              <Link to="/">
                <Button
                  variant="ghost"
                  className="flex items-center space-x-2 text-white hover:text-emerald-200 transition-all duration-300 hover:-translate-x-1 bg-white/10 backdrop-blur-sm"
                >
                  <ArrowLeft className="w-4 h-4" />
                  <span className="hidden sm:inline">Back to Menu</span>
                  <span className="sm:hidden">Back</span>
                </Button>
              </Link>
            </div>

            <Card className="shadow-2xl border-0 bg-white/95 backdrop-blur-sm animate-zoom-in">
              <CardHeader className="text-center pb-4 sm:pb-6">
                <div className="w-12 h-12 sm:w-16 sm:h-16 from-emerald-600 to-emerald-700 rounded-full flex items-center justify-center mx-auto mb-4 animate-rotate-in">
                  <img
                    src={logoUrl}
                    alt="Pixwi Pizza And Wings logo"
                    className="w-full h-full object-cover rounded-xl"
                  />
                </div>
                <CardTitle className="text-2xl sm:text-3xl font-bold text-gray-900 font-display animate-slide-in-down">
                  Welcome Back
                </CardTitle>
                <p
                  className="text-gray-600 mt-2 text-sm sm:text-base animate-slide-in-down"
                  style={{ animationDelay: "0.1s" }}
                >
                  Sign in to your {restaurant?.name} account
                </p>
              </CardHeader>

              <CardContent className="space-y-4 sm:space-y-6 animate-slide-in-up">
                {/* Google Login Button */}
                {/* <Button
                  onClick={handleGoogleLogin}
                  variant="outline"
                  className="w-full h-12 sm:h-14 border-gray-300 hover:bg-gray-50 text-gray-700 font-medium transition-all duration-300 hover:scale-105"
                  disabled={isLoading}
                >
                  <svg
                    className="w-4 h-4 sm:w-5 sm:h-5 mr-3"
                    viewBox="0 0 24 24"
                  >
                    <path
                      fill="#4285F4"
                      d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                    />
                    <path
                      fill="#34A853"
                      d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                    />
                    <path
                      fill="#FBBC05"
                      d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                    />
                    <path
                      fill="#EA4335"
                      d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                    />
                  </svg>
                  <span className="text-sm sm:text-base">
                    Continue with Google
                  </span>
                </Button> */}
                <GoogleOAuthProvider clientId={GoogleClientId}>
                  <GoogleLogin
                    onSuccess={handleGoogleLogin}
                    onError={() => console.log("Login Failed")}
                  />
                </GoogleOAuthProvider>
                {/* Guest Login Button */}
                <Button
                  onClick={handleGuestLogin}
                  variant="outline"
                  className="w-full h-12 sm:h-14 border-orange-500 text-orange-600 font-medium hover:bg-orange-50 transition-all duration-300 hover:scale-105 flex items-center justify-center"
                  disabled={isLoading}
                >
                  <svg
                    className="w-5 h-5 sm:w-6 sm:h-6 mr-3 text-orange-600"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    viewBox="0 0 24 24"
                    xmlns="http://www.w3.org/2000/svg"
                    aria-hidden="true"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M5.121 17.804A9 9 0 1118.88 6.195M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                    />
                  </svg>
                  <span className="text-sm sm:text-base">
                    Continue as Guest
                  </span>
                </Button>

                <div className="relative">
                  <div className="absolute inset-0 flex items-center">
                    <Separator className="w-full" />
                  </div>
                  <div className="relative flex justify-center text-xs uppercase">
                    <span className="bg-white px-2 text-gray-500">
                      Or continue with email
                    </span>
                  </div>
                </div>

                <form
                  onSubmit={handleSubmit}
                  className="space-y-4 sm:space-y-6"
                >
                  <div className="space-y-2">
                    <Label
                      htmlFor="email"
                      className="text-sm font-medium text-gray-700"
                    >
                      Email Address
                    </Label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4 sm:w-5 sm:h-5" />
                      <Input
                        id="email"
                        type="email"
                        placeholder="Enter your email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        className="pl-10 sm:pl-12 h-12 sm:h-14 border-gray-200 focus:border-emerald-500 focus:ring-emerald-500 text-sm sm:text-base transition-all duration-300"
                        required
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label
                      htmlFor="password"
                      className="text-sm font-medium text-gray-700"
                    >
                      Password
                    </Label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4 sm:w-5 sm:h-5" />
                      <Input
                        id="password"
                        type={showPassword ? "text" : "password"}
                        placeholder="Enter your password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        className="pl-10 sm:pl-12 pr-10 sm:pr-12 h-12 sm:h-14 border-gray-200 focus:border-emerald-500 focus:ring-emerald-500 text-sm sm:text-base transition-all duration-300"
                        required
                      />
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="absolute right-2 top-1/2 transform -translate-y-1/2 h-8 sm:h-10 w-8 sm:w-10 p-0"
                        onClick={() => setShowPassword(!showPassword)}
                      >
                        {showPassword ? (
                          <EyeOff className="w-4 h-4 sm:w-5 sm:h-5" />
                        ) : (
                          <Eye className="w-4 h-4 sm:w-5 sm:h-5" />
                        )}
                      </Button>
                    </div>
                  </div>

                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-3 sm:space-y-0">
                    <div className="flex items-center">
                      <input
                        id="remember-me"
                        name="remember-me"
                        type="checkbox"
                        className="h-4 w-4 text-emerald-600 focus:ring-emerald-500 border-gray-300 rounded"
                      />
                      <label
                        htmlFor="remember-me"
                        className="ml-2 block text-sm text-gray-700"
                      >
                        Remember me
                      </label>
                    </div>
                    <Button
                      variant="link"
                      className="text-emerald-600 hover:text-emerald-700 p-0 text-sm"
                    >
                      Forgot password?
                    </Button>
                  </div>
                  {errorMessage && (
                    <div className="p-3 text-red-600 bg-red-100 border border-red-300 rounded-md text-sm">
                      {errorMessage}
                    </div>
                  )}
                  <Button
                    type="submit"
                    className="w-full h-12 sm:h-14 btn-primary text-base sm:text-lg font-semibold"
                    disabled={isLoading}
                  >
                    {isLoading ? (
                      <div className="flex items-center space-x-2">
                        <div className="w-4 h-4 sm:w-5 sm:h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                        <span>Signing In...</span>
                      </div>
                    ) : (
                      <div className="flex items-center space-x-2">
                        <span>Sign In</span>
                        <ArrowRight className="w-4 h-4 sm:w-5 sm:h-5" />
                      </div>
                    )}
                  </Button>
                </form>

                <div className="mt-6 sm:mt-8 text-center">
                  <p className="text-gray-600 text-sm sm:text-base">
                    Don't have an account?{" "}
                    <Link
                      to={`/signup${
                        redirectTo !== "/" ? `?redirect=${redirectTo}` : ""
                      }`}
                      className="font-medium text-emerald-600 hover:text-emerald-700 transition-colors duration-300"
                    >
                      Sign up here
                    </Link>
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
      <Footer />
    </>
  );
}
